import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Stack,
  TextField,
  useTheme,
  Box,
  CircularProgress,
  IconButton,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import {
  useCreateAssociatedCampaignTargetMutation,
  useUpdateAssociatedCampaignTargetMutation,
  useGetAssociatedCampaignsQuery,
  useGetBriefingByIdQuery,
  AssociatedCampaignTarget,
} from "../../store/apis/briefingApi";
import { uploadBriefingTargetImage } from "../../firebase/firebaseStorage";
import { useGetCompanyAudiencesQuery } from "../../store/apis/companyApi";
import { components } from "../../types/api";
import { setToastMessage } from "../../store/slices/app/appSlice";
import { LanguageSelect } from "../common/LanguageSelect";

type BriefingSex = components["schemas"]["BriefingSex"];

type ApiErrorWithMessage = { data?: { message?: string } };

const extractApiErrorMessage = (error: unknown): string | undefined => {
  if (
    typeof error === "object" &&
    error !== null &&
    "data" in error &&
    typeof (error as ApiErrorWithMessage).data?.message === "string"
  ) {
    return (error as ApiErrorWithMessage).data?.message;
  }
  return undefined;
};

type TargetFormState = {
  sex: BriefingSex;
  minimumAge: string;
  maximumAge: string;
  languages: string[];
  geography: string;
  technology: string;
  interests: string;
  dataAudience: string;
  exclusions: string;
  notes: string;
  imageUrl: string;
};

type TargetFormErrors = Partial<Record<keyof TargetFormState, string>>;

const defaultFormState: TargetFormState = {
  sex: "BOTH",
  minimumAge: "",
  maximumAge: "",
  languages: ["ANY"],
  geography: "",
  technology: "",
  interests: "",
  dataAudience: "",
  exclusions: "",
  notes: "",
  imageUrl: "",
};

const TARGET_FIELDS = [
  ["geography", "briefingsView.target.geography", "Geography"],
  ["technology", "briefingsView.target.technology", "Technology"],
  ["interests", "briefingsView.target.interests", "Interests"],
  ["dataAudience", "briefingsView.target.dataAudience", "Data audience"],
  ["exclusions", "briefingsView.target.exclusions", "Exclusions"],
  ["notes", "briefingsView.target.notes", "Notes"],
] as const;

interface ManageTargetDialogProps {
  open: boolean;
  mode: "create" | "edit";
  briefingId: string;
  associatedId: string;
  target?: AssociatedCampaignTarget | null;
  onClose: () => void;
  onCompleted?: () => void;
}

const ManageTargetDialog: React.FC<ManageTargetDialogProps> = ({
  open,
  mode,
  briefingId,
  associatedId,
  target,
  onClose,
  onCompleted,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const [formState, setFormState] = useState<TargetFormState>(defaultFormState);
  const [errors, setErrors] = useState<TargetFormErrors>({});
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  const [createTarget, { isLoading: isCreating }] =
    useCreateAssociatedCampaignTargetMutation();
  const [updateTarget, { isLoading: isUpdating }] =
    useUpdateAssociatedCampaignTargetMutation();

  const isSubmitting = isCreating || isUpdating;

  useEffect(() => {
    if (open) {
      if (mode === "edit" && target) {
        setFormState({
          sex: (target.sex as BriefingSex) ?? "BOTH",
          minimumAge: String(target.minimumAge ?? ""),
          maximumAge: String(target.maximumAge ?? ""),
          languages: target.language
            ? target.language.split(",").map((c) => c.trim())
            : [],
          geography: target.geography ?? "",
          technology: target.technology ?? "",
          interests: target.interests ?? "",
          dataAudience: target.dataAudience ?? "",
          exclusions: target.exclusions ?? "",
          notes: target.notes ?? "",
          imageUrl: target.imageUrl ?? "",
        });
      } else {
        setFormState(defaultFormState);
      }
      setErrors({});
    }
  }, [open, mode, target]);

  const sexOptions = useMemo<BriefingSex[]>(() => ["MAN", "WOMAN", "BOTH"], []);

  const { data: associatedCampaigns } = useGetAssociatedCampaignsQuery(
    { id: briefingId },
    { skip: !open }
  );

  const { data: briefingData } = useGetBriefingByIdQuery(
    { id: briefingId },
    { skip: !open || !briefingId }
  );
  const companyId = briefingData?.data?.companyId;

  const { data: audiencesData } = useGetCompanyAudiencesQuery(
    companyId as string,
    { skip: !open || !companyId }
  );
  const companyAudiences = audiencesData?.data ?? [];

  const copyableCampaigns = useMemo(() => {
    return (
      associatedCampaigns?.data?.filter(
        (c) =>
          c.id !== associatedId && c.target !== null && c.target !== undefined
      ) ?? []
    );
  }, [associatedCampaigns, associatedId]);

  const handleCopyTarget = (campaignId: string) => {
    const campaignToCopy = copyableCampaigns.find((c) => c.id === campaignId);
    if (campaignToCopy?.target) {
      const copiedTarget = campaignToCopy.target;
      setFormState({
        sex: (copiedTarget.sex as BriefingSex) ?? "BOTH",
        minimumAge: String(copiedTarget.minimumAge ?? ""),
        maximumAge: String(copiedTarget.maximumAge ?? ""),
        languages: copiedTarget.language
          ? copiedTarget.language.split(",").map((c) => c.trim())
          : [],
        geography: copiedTarget.geography ?? "",
        technology: copiedTarget.technology ?? "",
        interests: copiedTarget.interests ?? "",
        dataAudience: copiedTarget.dataAudience ?? "",
        exclusions: copiedTarget.exclusions ?? "",
        notes: copiedTarget.notes ?? "",
        imageUrl: copiedTarget.imageUrl ?? "",
      });
      setErrors({});
      dispatch(
        setToastMessage({
          message: t(
            "briefingsView.targetCopied",
            "Target copied successfully"
          ),
          severity: "success",
        })
      );
    }
  };

  const handleCopyFromAudience = (audienceId: string) => {
    const audienceToCopy = companyAudiences.find((a) => a.id === audienceId);
    if (audienceToCopy) {
      setFormState((prev) => ({
        ...prev,
        sex: (audienceToCopy.sex as BriefingSex) ?? prev.sex,
        minimumAge: audienceToCopy.minimumAge
          ? String(audienceToCopy.minimumAge)
          : prev.minimumAge,
        maximumAge: audienceToCopy.maximumAge
          ? String(audienceToCopy.maximumAge)
          : prev.maximumAge,
        languages: audienceToCopy.language
          ? audienceToCopy.language.split(",").map((c) => c.trim())
          : prev.languages,
        geography: audienceToCopy.geography ?? prev.geography,
        technology: audienceToCopy.technology ?? prev.technology,
        interests: audienceToCopy.interests ?? prev.interests,
        dataAudience: audienceToCopy.dataAudience ?? prev.dataAudience,
        exclusions: audienceToCopy.exclusions ?? prev.exclusions,
        notes: audienceToCopy.notes ?? prev.notes,
        imageUrl: audienceToCopy.imageUrl ?? prev.imageUrl,
      }));
      setErrors({});
      dispatch(
        setToastMessage({
          message: t(
            "briefingsView.audienceImported",
            "Audience applied successfully. Please review the populated fields."
          ),
          severity: "success",
        })
      );
    }
  };

  const handleClose = () => {
    if (isSubmitting) {
      return;
    }
    setErrors({});
    onClose();
  };

  const handleFieldChange = <K extends keyof TargetFormState>(
    field: K,
    value: TargetFormState[K]
  ) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setIsUploadingImage(true);
      const url = await uploadBriefingTargetImage(file, associatedId);
      setFormState((prev) => ({ ...prev, imageUrl: url }));
    } catch (error) {
      console.error("Failed to upload image", error);
      dispatch(
        setToastMessage({
          message: t(
            "briefingsView.targetImageUploadError",
            "Failed to upload image"
          ),
          severity: "error",
        })
      );
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleRemoveImage = () => {
    setFormState((prev) => ({ ...prev, imageUrl: "" }));
  };

  const validateForm = (): boolean => {
    const nextErrors: TargetFormErrors = {};

    if (!formState.minimumAge.trim()) {
      nextErrors.minimumAge = t(
        "validation.required",
        "This field is required"
      );
    } else if (Number.isNaN(Number(formState.minimumAge))) {
      nextErrors.minimumAge = t(
        "validation.invalidNumber",
        "Enter a valid number"
      );
    }

    if (!formState.maximumAge.trim()) {
      nextErrors.maximumAge = t(
        "validation.required",
        "This field is required"
      );
    } else if (Number.isNaN(Number(formState.maximumAge))) {
      nextErrors.maximumAge = t(
        "validation.invalidNumber",
        "Enter a valid number"
      );
    }

    if (!formState.geography.trim()) {
      nextErrors.geography = t("validation.required", "This field is required");
    }

    if (!formState.interests.trim()) {
      nextErrors.interests = t("validation.required", "This field is required");
    }

    const minAge = Number(formState.minimumAge);
    const maxAge = Number(formState.maximumAge);
    if (!Number.isNaN(minAge) && !Number.isNaN(maxAge) && minAge > maxAge) {
      nextErrors.minimumAge = t(
        "briefingsView.targetForm.minGreaterThanMax",
        "Minimum age cannot be greater than maximum age"
      );
      nextErrors.maximumAge = t(
        "briefingsView.targetForm.minGreaterThanMax",
        "Minimum age cannot be greater than maximum age"
      );
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const buildPayload = () => ({
    sex: formState.sex,
    minimumAge: Number(formState.minimumAge),
    maximumAge: Number(formState.maximumAge),
    language:
      formState.languages.length > 0
        ? formState.languages.join(",")
        : undefined,
    geography: formState.geography.trim(),
    technology: formState.technology.trim() || undefined,
    interests: formState.interests.trim(),
    dataAudience: formState.dataAudience.trim() || undefined,
    exclusions: formState.exclusions.trim() || undefined,
    notes: formState.notes.trim() || undefined,
    imageUrl: formState.imageUrl.trim() || undefined,
  });

  const handleSubmit = async (): Promise<void> => {
    if (!validateForm()) {
      return;
    }

    try {
      const body = buildPayload();
      if (mode === "create") {
        await createTarget({
          params: { briefingId, associatedId },
          body,
        }).unwrap();
        dispatch(
          setToastMessage({
            message: t(
              "briefingsView.targetCreateSuccess",
              "Targeting details created successfully"
            ),
            severity: "success",
          })
        );
      } else {
        await updateTarget({
          params: { briefingId, associatedId },
          body,
        }).unwrap();
        dispatch(
          setToastMessage({
            message: t(
              "briefingsView.targetUpdateSuccess",
              "Targeting details updated successfully"
            ),
            severity: "success",
          })
        );
      }

      onCompleted?.();
      onClose();
    } catch (error) {
      const apiMessage = extractApiErrorMessage(error);
      dispatch(
        setToastMessage({
          message:
            apiMessage ??
            (mode === "create"
              ? t(
                  "briefingsView.targetCreateError",
                  "Failed to create targeting details"
                )
              : t(
                  "briefingsView.targetUpdateError",
                  "Failed to update targeting details"
                )),
          severity: "error",
        })
      );
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ fontWeight: 700 }}>
        {mode === "create"
          ? t("briefingsView.createTargetTitle", "Create targeting details")
          : t("briefingsView.editTargetTitle", "Edit targeting details")}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Grid container spacing={2}>
            {companyAudiences.length > 0 && (
              <Grid item xs={12} md={copyableCampaigns.length > 0 ? 6 : 12}>
                <TextField
                  select
                  fullWidth
                  label={t(
                    "briefingsView.copyTargetFromAudience",
                    "Populate from Company Audience"
                  )}
                  value=""
                  onChange={(e) => handleCopyFromAudience(e.target.value)}
                  size="small"
                  helperText={t(
                    "briefingsView.copyTargetAudienceHelper",
                    "Select an audience to populate fields below"
                  )}
                >
                  <MenuItem value="" disabled>
                    <em>{t("common.select", "Select...")}</em>
                  </MenuItem>
                  {companyAudiences.map((audience) => (
                    <MenuItem key={audience.id} value={audience.id}>
                      {audience.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
            {copyableCampaigns.length > 0 && (
              <Grid item xs={12} md={companyAudiences.length > 0 ? 6 : 12}>
                <TextField
                  select
                  fullWidth
                  label={t(
                    "briefingsView.copyTargetFrom",
                    "Copy from another line"
                  )}
                  value=""
                  onChange={(e) => handleCopyTarget(e.target.value)}
                  size="small"
                  helperText={t(
                    "briefingsView.copyTargetHelper",
                    "Select a line to copy its targeting details"
                  )}
                >
                  <MenuItem value="" disabled>
                    <em>{t("common.select", "Select...")}</em>
                  </MenuItem>
                  {copyableCampaigns.map((campaign) => (
                    <MenuItem key={campaign.id} value={campaign.id}>
                      {campaign.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
          </Grid>
          <Alert severity="info">
            {t(
              "briefingsView.targetForm.instructions",
              "Provide audience targeting information for this associated campaign."
            )}
          </Alert>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                label={t("briefingsView.target.sex", "Sex")}
                value={formState.sex}
                onChange={(event) =>
                  handleFieldChange("sex", event.target.value as BriefingSex)
                }
                size="small"
              >
                {sexOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option === "BOTH"
                      ? t("briefingsView.target.sexBoth", "Both")
                      : option === "MAN"
                        ? t("briefingsView.target.sexMan", "Men")
                        : t("briefingsView.target.sexWoman", "Women")}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                type="number"
                fullWidth
                label={t("briefingsView.target.minimumAge", "Minimum age")}
                value={formState.minimumAge}
                onChange={(event) =>
                  handleFieldChange("minimumAge", event.target.value)
                }
                error={Boolean(errors.minimumAge)}
                helperText={errors.minimumAge}
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                type="number"
                fullWidth
                label={t("briefingsView.target.maximumAge", "Maximum age")}
                value={formState.maximumAge}
                onChange={(event) =>
                  handleFieldChange("maximumAge", event.target.value)
                }
                error={Boolean(errors.maximumAge)}
                helperText={errors.maximumAge}
                size="small"
              />
            </Grid>
            {TARGET_FIELDS.filter(([field]) => field !== "notes").map(
              ([field, key, fallback]) => (
                <Grid item xs={12} md={4} key={field}>
                  <TextField
                    fullWidth
                    label={t(key, fallback)}
                    value={formState[field as keyof TargetFormState]}
                    onChange={(event) =>
                      handleFieldChange(field, event.target.value)
                    }
                    size="small"
                    multiline={field === "notes"}
                    minRows={field === "notes" ? 3 : undefined}
                    error={Boolean(errors[field as keyof TargetFormState])}
                    helperText={errors[field as keyof TargetFormState]}
                  />
                </Grid>
              )
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t("briefingsView.target.notes", "Notes")}
                value={formState.notes}
                onChange={(event) =>
                  handleFieldChange("notes", event.target.value)
                }
                size="small"
                multiline
                minRows={3}
                error={Boolean(errors.notes)}
                helperText={errors.notes}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                {t("briefingsView.target.image", "Attachment Image")}
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center">
                {formState.imageUrl ? (
                  <Box sx={{ position: "relative", display: "inline-block" }}>
                    <img
                      src={formState.imageUrl}
                      alt="Target attachment"
                      style={{
                        height: 100,
                        width: "auto",
                        borderRadius: 4,
                        objectFit: "cover",
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={handleRemoveImage}
                      sx={{
                        position: "absolute",
                        top: -8,
                        right: -8,
                        bgcolor: "background.paper",
                        boxShadow: 1,
                        "&:hover": { bgcolor: "background.paper" },
                      }}
                    >
                      <DeleteIcon fontSize="small" color="error" />
                    </IconButton>
                  </Box>
                ) : (
                  <Button
                    component="label"
                    variant="outlined"
                    startIcon={
                      isUploadingImage ? (
                        <CircularProgress size={20} />
                      ) : (
                        <CloudUploadIcon />
                      )
                    }
                    disabled={isUploadingImage}
                  >
                    {isUploadingImage
                      ? t("common.uploading", "Uploading...")
                      : t("briefingsView.target.uploadImage", "Upload Image")}
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={handleImageUpload}
                    />
                  </Button>
                )}
              </Stack>
            </Grid>
            <Grid item xs={12} md={4}>
              <LanguageSelect
                multiple
                label={t("briefingsView.target.language", "Language")}
                value={formState.languages}
                onChange={(value) =>
                  handleFieldChange("languages", value as string[])
                }
                size="small"
              />
            </Grid>
          </Grid>
        </Stack>
      </DialogContent>
      <DialogActions
        sx={{ px: 3, py: 2, borderTop: `1px solid ${theme.palette.divider}` }}
      >
        <Button onClick={handleClose} disabled={isSubmitting}>
          {t("common.cancel", "Cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          {mode === "create"
            ? t("briefingsView.targetCreateCta", "Create target")
            : t("briefingsView.targetUpdateCta", "Save changes")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ManageTargetDialog;
